<template>
    <div
        :class="classContentButton"
        class="content-button"
    >
        <AffirmWidget
            v-if="affirm"
            :refresh-key="null"
        >
            <ThButton
                :[iconPosition]="icon"
                :color="buttonColor"
                :fixed-width="isFixedWidth"
                :size="ButtonSizes[data.style.size]"
                :text-size="linkSize"
                :type="ButtonTypes[data.style.type]"
                class="affirm-site-modal"
                data-page-type="landing"
                v-bind="bind"
                :tracking
            >
                {{ data.text }}
            </ThButton>
        </AffirmWidget>

        <ThButton
            v-else
            :[iconPosition]="icon"
            :color="buttonColor"
            :fixed-width="isFixedWidth"
            :size="ButtonSizes[data.style.size]"
            :text-size="linkSize"
            :type="ButtonTypes[data.style.type]"
            :tracking
            v-bind="bind"
        >
            {{ data.text }}
        </ThButton>
    </div>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import type { Color, ComponentButtonFragment, LinkItem } from '@/graphql/api'
import type { Icons } from '@/components/ui/icon'
import {
    cmsColorToButtonColor,
    CmsToButtonSizes as ButtonSizes,
    CmsToButtonTypes as ButtonTypes,
    useCmsLink,
} from '@/components/cms'
import type { ButtonColors, ButtonTextSize } from '@/components/ui/buttons'
import { assertNever } from '@/helpers'
import { cmsIconToThIcon } from '@/helpers/icon'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import AffirmWidget from '@/components/rings/AffirmWidget.vue'
import type { ButtonTracking } from '@/components/ui/buttons/useThButton.ts'

export type PageType =
    | 'product-details'
    | 'product-listing'
    | 'system-page'
    | 'post'
    | 'questionnaire'
    | 'page'
    | 'faceted-content'

const props = defineProps<{
    data: ComponentButtonFragment
    pageType: PageType
    color?: Color
}>()

const affirm = toRef(props.data.link.item.__typename === 'LinkItemAffirm')

// TODO prepend locale to links

const buttonColor = computed(
    (): ButtonColors => cmsColorToButtonColor(props.data.style.color)
)

const param3 = (typename: LinkItem['__typename']): string => {
    switch (typename) {
        case 'LinkItemAffirm':
            return 'open_affirm_prequalify'
        case 'LinkItemBookingForm':
            return 'open_booking_form'
        case 'LinkItemOpenContactForm':
            return 'open_contact_form'
        case 'LinkItemOpenEnquiryForm':
            return 'open_enquiry_form'
        case 'Post':
        case 'Page':
            return 'internal'
        case 'LinkItemOpenIntercom':
            return 'open_intercom'
        case 'LinkItemOpenSpeakToExpertModal':
            return 'open_speak_to_expert_modal'
        case 'LinkItemCustom':
            return 'custom'
        case 'Questionnaire':
            return 'open_questionnaire'
        case 'LinkItemEngagementRingRecommendations':
            return 'open_recommendation_plp'
        default:
            return assertNever(typename, typename)
    }
}
const tracking = computed(
    (): ButtonTracking => ({
        eventName: 'cta_button_click',
        param1: props.data.text,
        param2: props.pageType,
        param3: param3(props.data.link.item.__typename),
    })
)
const linkSize = computed((): ButtonTextSize => {
    switch (props.data.style.linkSize) {
        case 12:
        case 14:
        case 16:
        case 19:
            return props.data.style.linkSize
        default:
            return 14
    }
})

const isFixedWidth = computed(() => props.data.style.width === 'FIXED')

const icon = computed((): Icons | undefined => {
    const icon = props.data.icon

    if (!icon) {
        return undefined
    }

    return cmsIconToThIcon(icon.icon)
})

const iconPosition = computed((): 'iconLeading' | 'iconTrailing' => {
    const icon = props.data.icon

    if (!icon) {
        return 'iconLeading'
    }

    switch (icon.position) {
        case 'LEFT':
            return 'iconLeading'
        case 'RIGHT':
            return 'iconTrailing'
        case 'CENTER':
            return 'iconLeading'
        default:
            return assertNever(icon.position, 'iconLeading')
    }
})

const bind = useCmsLink(toRef(() => props.data.link))

const classContentButton = computed(() => ({
    [props.data.alignment.toLocaleLowerCase()]: true,
    [`top-spacing-${props.data.style.topSpacing.toLocaleLowerCase()}`]: true,
    [`bottom-spacing-${props.data.style.bottomSpacing.toLocaleLowerCase()}`]: true,
}))

//    alignment: HorizontalAlignment; // todo
// eventTracking?: Maybe<ButtonEventTracking>; todo tracking - in @/components/ui/buttons/useThButton.ts
// icon?: Maybe<ButtonIcon>;
// link: Link;
// style: {
//     __typename?: 'ButtonStyle';
//     bottomSpacing: VerticalSpacing; todo
//     color: Color;
//     design: ButtonDesign;
//     linkSize: Scalars['Int'];
//     size: ButtonSize;
//     topSpacing: VerticalSpacing; todo
//     type: ButtonType;
//     width: ButtonWidth; todo
// };
// text: Scalars['String'];
</script>

<style lang="scss" scoped>
.content-button {
    width: 100%;
    max-width: 680px;
    margin: 0 auto;
    display: flex;

    &.left {
        justify-content: left;
    }

    &.center {
        justify-content: center;
    }

    &.right {
        justify-content: right;
    }

    &.top-spacing-normal {
        margin-top: 20px;
    }

    &.top-spacing-large {
        margin-top: 50px;
    }

    &.bottom-spacing-normal {
        margin-bottom: 20px;
    }

    &.bottom-spacing-large {
        margin-bottom: 50px;
    }
}
</style>
